import React = require("react");
import { combineHandlers, combineRefs, forwardRef, stopPropagation } from "@zap/utils/lib/ReactHelpers";
import { Uuid } from "@zap/utils/lib/Uuid";
import { memo, MouseEvent, KeyboardEvent, ReactNode, Ref, RefObject, useRef, useState } from "react";
import { StyleCollection } from "stylemap";
import { noSpacing, Row } from "./Box";
import { Hsla } from "./color";
import { activeBackgroundOpacity, darkGreyColor, disabledBackground, disabledForeground, duration, errorColor, focusBackgroundOpacity, focusRingOpacity, greyColor, highlightColor, hoverBackgroundOpacity, roundedBorders, whiteColor, zIndexes } from "./CommonStyles";
import { useDisabled } from "./Disabling";
import { FocusHelper, IFocusHelper } from "./FocusHelper";
import { keymap } from "./Keyboard";
import { animation, boxShadow, CSSProperties, style, Styled, Styles, transition } from "./styling";

export type ClickableColor = keyof typeof colorStyles;
export type ClickEvent = MouseEvent | KeyboardEvent;

export interface IClickableProps {
    round?: boolean;
    color?: ClickableColor;
    invert?: boolean;
    disabled?: boolean;
    children: ReactNode | ((helper: IFocusHelper) => ReactNode);
    hideRipple?: boolean;
    selected?: boolean;
    styles?: StyleCollection;
    className?: string;
    inline?: CSSProperties;
    tabIndex?: number;
    onClick?: (e: ClickEvent) => void;
    onDoubleClick?: (e: ClickEvent) => void;
    onMouseEnter?: (e: MouseEvent) => void;
    onMouseLeave?: (e: MouseEvent) => void;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    preventFocus?: boolean;
    ignoreKeyboardEvents?: boolean;
    forceHoverStyling?: boolean;
}

export const Clickable = forwardRef(function Clickable(
    {
        round = false,
        color = 'default',
        invert = false,
        disabled = false,
        hideRipple = false,
        selected,
        preventFocus = false,
        ignoreKeyboardEvents = false,
        forceHoverStyling,
        onClick = () => { },
        onDoubleClick = () => { },
        onMouseEnter = () => { },
        onMouseLeave = () => { },
        onFocus,
        onBlur,
        onKeyDown,
        styles, className, inline, tabIndex, children
    }: IClickableProps,
    ref: Ref<HTMLDivElement>) {

    let selectable = typeof selected == 'boolean';

    disabled = useDisabled(disabled);

    let colored = getColorStyles();

    let wrapperRef = useRef<HTMLDivElement>(null);
    let { ripples, addRipple } = useRipples(wrapperRef, colored.color);

    return <FocusHelper disabled={disabled}>
        {(helper, focusRef) =>
            <Row inline spacing="half"
                ref={combineRefs(ref, wrapperRef, focusRef)}
                data-testid={testIds.clickable}
                tabIndex={tabIndex}
                className={className}
                styles={[
                    clickableStyle,
                    disabled ? colored.disabled : colored.enabled,
                    round ? circle : square,
                    selected && colored.selected,
                    helper.isFocused && (selectable ? colored.selectableFocused : colored.unselectableFocused),
                    hideRipple && !disabled && colored.hiddenRipple,
                    forceHoverStyling && forceHoverStyle,
                    styles
                ]}
                inlineStyles={inline}
                onClick={stopPropagation(onClickOverride)}
                onDoubleClick={stopPropagation(onDoubleClickOverride)}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseDown={e => handleMouseDown(e)}
                onKeyDown={combineHandlers(onKeyDown, keymap({ ' ': onActivate, Enter: onActivate }))}
                {...helper.getFocusProps({ onFocus, onBlur })}>
                {typeof children == 'function' ? children(helper) : children}
                {ripples}
            </Row>
        }
    </FocusHelper>;

    function onActivate(e: KeyboardEvent) {
        if (!ignoreKeyboardEvents) {
            e.stopPropagation();
            activate(e);
        }
    }

    function onClickOverride(e: MouseEvent<HTMLDivElement>) {
        if (e.button == 0)
            activate(e);
    }

    function onDoubleClickOverride(e: MouseEvent<HTMLDivElement>) {
        onDoubleClick(e);
    }

    function activate(e: ClickEvent) {
        if (!disabled) {
            if (!hideRipple)
                addRipple(rippleOrigin(e));
            onClick(e);
        }
    }

    function handleMouseDown(e: MouseEvent<HTMLDivElement>) {
        if (preventFocus) e.preventDefault();
    }

    function getColorStyles() {
        return invert
            ? invertedColorStyles[color]
            : colorStyles[color];
    }

    function rippleOrigin(e: ClickEvent): { clientX?: number, clientY?: number } {
        let mouse = e as MouseEvent;
        return ({ clientX: mouse.clientX, clientY: mouse.clientY });
    }
});

let clickableStyle = style('clickable', {
    position: 'relative',
    overflow: 'hidden',
    WebkitTapHighlightColor: 'transparent', // prevent touch flash in chrome
    zIndex: zIndexes.page,
    transition: transition('background', duration.small),
    ':focus': {
        outline: 'none'
    }
});

let forceHoverStyle = style('clickable-forceHover', {});

let colorStyles = {
    default: backgroundStyles('default', highlightColor, false),
    dangerous: backgroundStyles('dangerous', errorColor, false),
    grey: backgroundStyles('grey', greyColor, false),
    darkGrey: backgroundStyles('darkGrey', darkGreyColor, false),
    black: backgroundStyles('black', '#000000', false),
    white: backgroundStyles('white', '#ffffff', false)
};

let invertedColorStyles = {
    default: backgroundStyles('default', highlightColor, true),
    dangerous: backgroundStyles('dangerous', errorColor, true),
    grey: backgroundStyles('grey', greyColor, true),
    darkGrey: backgroundStyles('darkGrey', darkGreyColor, true),
    black: backgroundStyles('black', '#000000', true),
    white: backgroundStyles('white', '#ffffff', false)
};

function backgroundStyles(name: string, color: string, invert = false) {
    let hsl = Hsla.parse(color)!;

    let foreground = invert ? whiteColor : color;

    let fade = (opacity: number) => hsl.fade(invert ? 0.9 - opacity : opacity).toString();

    let enabled = style(`clickable-${name}-enabled`, {
        background: invert ? color : 'transparent',
        color: foreground,
        cursor: 'pointer',
        ':hover': {
            background: fade(hoverBackgroundOpacity)
        },
        $: {
            [`&.${forceHoverStyle}`]: {
                background: fade(hoverBackgroundOpacity)
            }
        }
    });

    let selected = style(`clickable-${name}-selected`, {
        background: fade(focusBackgroundOpacity)
    });

    let disabled = style(`clickable-${name}-disabled`, {
        background: invert ? disabledBackground : 'transparent',
        color: disabledForeground,
        cursor: 'default'
    });

    let unselectableFocused = style(`clickable-${name}-focused`, {
        background: fade(focusBackgroundOpacity),
        ':hover': {
            background: fade(focusBackgroundOpacity)
        }
    });

    let selectableFocused = style(`clickable-${name}-selectableFocused`, {
        boxShadow: `inset ${boxShadow(0, 0, 0, 1, invert ? hsl.darker().toString() : fade(focusRingOpacity))}`,
        ':hover': {
            background: fade(focusBackgroundOpacity)
        }
    })

    let hiddenRipple = style(`clickable-${name}-noRipple`, {
        ':active': {
            background: fade(activeBackgroundOpacity),
            transition: 'none'
        }
    });

    return { color: foreground, selected, unselectableFocused, selectableFocused, enabled, disabled, hiddenRipple };
}

let square = style('clickable-square', {
    ...roundedBorders
});

let circle = style('clickable-round', {
    borderRadius: '50%'
});

export function useRipples<T extends HTMLElement = HTMLDivElement>(ref: RefObject<T>, color: string, sizeOverride?: number, center?: boolean) {
    let [ripples, setRipples] = useState([] as JSX.Element[]);

    return { ripples, addRipple };

    function addRipple(e: { clientX?: number, clientY?: number }) {
        let key = Uuid.create();
        let ripple = <Ripple key={key} {...getRippleProps(e, key)} />;
        setRipples(rs => rs.concat(ripple));
    }

    function getRippleProps(e: { clientX?: number, clientY?: number }, key: string): IRippleProps {
        let el = ref.current!;
        let rect = el.getBoundingClientRect();
        let size = sizeOverride ?? Math.max(el.clientWidth, el.clientHeight);
        let left = center
            ? 0
            : (e.clientX || rect.left + rect.width / 2) - rect.left - size / 2
        let top = center
            ? 0
            : (e.clientY || rect.top + rect.height / 2) - rect.top - size / 2;

        return {
            left,
            top,
            color,
            size,
            animationDone: () => removeRipple(key)
        }
    }

    function removeRipple(key: string) {
        setRipples(rs => rs.filter(r => r.key !== key));
    }

}

interface IRippleProps {
    left: number,
    top: number,
    size: number,
    color: Styles['color'],
    animationDone(): void
}

const Ripple = memo(function Ripple(props: IRippleProps) {
    return <Styled.div onAnimationEnd={props.animationDone} data-testid={testIds.ripple} styles={[rippleStyle, noSpacing]} inline={customRippleStyles(props)} />;
});

function customRippleStyles({ size, left, top, color }: IRippleProps): CSSProperties {
    return {
        left: left,
        top: top,
        background: String(color),
        height: size,
        width: size
    };
}

let rippleOpacity = 0.2;

export let rippleStyle = style('ripple', {
    position: 'absolute',
    transition: 'none',
    borderRadius: '100%',
    opacity: 0,
    zIndex: zIndexes.belowPage,
    animation: animation({
        0: {
            opacity: rippleOpacity,
            transform: 'scale(0)'
        },
        50: {
            opacity: 0.7 * rippleOpacity
        },
        100: {
            opacity: 0,
            transform: 'scale(2)'
        }
    }, duration.medium, 'ease-out', 0, 1)
});

export const testIds = {
    clickable: 'clickable',
    ripple: 'ripple'
};

export interface IClickableGroupProps<T> {
    selectedItem?: T;
    items: T[];
    onSelect(item: T): void;
    itemKey(item: T): string;
    children(item: T, helper: IFocusHelper): ReactNode;
    round?: boolean;
}

export function ClickableGroup<T>(props: IClickableGroupProps<T>) {
    return <>
        {props.items.map(item => {
            let itemKey = props.itemKey(item);

            return <Clickable key={itemKey} onClick={() => props.onSelect(item)} selected={isSelected(itemKey)} round={props.round}>
                {helper => props.children(item, helper)}
            </Clickable>
        })}
    </>;

    function isSelected(itemKey: string) {
        return props.selectedItem === undefined
            ? false
            : props.itemKey(props.selectedItem) === itemKey;
    }
}
